import { useCallback, useEffect, useState, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import CompanyHeader from '@/components/company/CompanyHeader';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import FormBuilderWithLayout, { FormField } from '@/components/company/FormBuilderWithLayout';
import SearchBar from '@/components/company/filters/SearchBar';
import FilterSelect from '@/components/company/filters/FilterSelect';
import ClearFilterButton from '@/components/company/filters/ClearFilterButton';
import useTenantState from '@/states/tenant.state';
import useJobDescriptionState, { IJobDescription } from '@/states/job.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

export default function KPI() {
  const { currentTenant } = useTenantState();
  const {
    getJobDescriptions,
    getJobDescriptionsFull,
    paginatedData,
    jobDescriptionsFull,
    updateJobDescription,
    isLoading,
  } = useJobDescriptionState();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [formMode, setFormMode] = useState<'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IJobDescription | null>(null);

  const formControl = useForm<IJobDescription>();
  const { handleSubmit, reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'kpi',
    order: 'asc',
  });

  // Debounce search term: wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      await getJobDescriptions(currentTenant.id, {
        page: pageConfig.page,
        page_size: pageConfig.limit,
        org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
        search: debouncedSearchTerm || undefined,
        orderBy: pageConfig.orderBy,
        order: pageConfig.order,
      });
    } catch {
      toast.error('Failed to fetch data');
    }
  }, [currentTenant?.id, pageConfig.page, pageConfig.limit, pageConfig.orderBy, pageConfig.order, selectedRole, debouncedSearchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (currentTenant?.id) {
      getJobDescriptionsFull(currentTenant.id);
    }
  }, [currentTenant?.id]);

  const displayData = paginatedData || {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
  };

  const roleOptions = Array.from(
    new Map(
      jobDescriptionsFull.map(jd => [
        jd.org_chart_node_id,
        { label: jd.org_chart_node?.name ?? '-', value: String(jd.org_chart_node_id) },
      ]),
    ).values(),
  );

  const columns: MainTableColumn[] = [
    { name: 'org_chart_node.name', label: 'Role', render: (_: unknown, row) => row?.org_chart_node?.name ?? '-' },
    { name: 'kpi', label: 'KPI' },
    {
      name: 'kpi_measurement',
      label: 'Measurement',
      render: (val: string) => <div className="max-w-xs truncate" dangerouslySetInnerHTML={{ __html: val || '-' }} />,
    },
  ];

  const fields: FormField[] = [
    { name: 'org_chart_node.name', label: 'Role', type: 'text', disable: true },
    { name: 'kpi', label: 'KPI', type: 'text', required: true },
    {
      name: 'kpi_measurement',
      label: 'Measurement',
      type: 'richtext',
      required: true,
      placeholder: 'Define measurement criteria...',
      colSpan: 2,
    },
  ];

  const handleSave = async (data: IJobDescription) => {
    if (!currentTenant?.id || !selectedRow?.id) return;
    try {
      await updateJobDescription(currentTenant.id, selectedRow.id, {
        kpi: data.kpi,
        kpi_measurement: data.kpi_measurement,
      });
      toast.success('KPI updated successfully');
      setFormMode(null);
      setSelectedRow(null);
      await fetchData();
    } catch {
      toast.error('Failed to update KPI');
    }
  };

  return (
    <section className="w-full">
      <CompanyHeader
        title="Key Performance Indicators (KPI)"
        description="Manage KPIs and performance metrics for different roles in your organization."
      />
      <div className="mx-5 border-b border-gray-200" />

      {formMode === 'edit' && selectedRow && (
        <FormProvider {...formControl}>
          <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="mt-4">
            <FormBuilderWithLayout
              description="Edit KPI"
              fields={fields}
              onSubmit={handleSubmit(handleSave)}
              onCancel={() => {
                setFormMode(null);
                setSelectedRow(null);
              }}
              submitLabel="Update"
              cancelLabel="Cancel"
            />
          </form>
        </FormProvider>
      )}

      <div className="flex justify-start mt-5 px-5 gap-2">
        <SearchBar
          value={searchTerm}
          onChange={val => {
            setSearchTerm(val);
            if (!val) setDebouncedSearchTerm('');
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Search KPI..."
          className="w-72"
        />
        <FilterSelect
          label="Role"
          options={roleOptions}
          value={selectedRole}
          onChange={val => {
            setSelectedRole(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <ClearFilterButton
          onClear={() => {
            setSearchTerm('');
            setSelectedRole('');
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
      </div>

      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={{
          content: displayData.content as unknown as { [key: string]: unknown }[],
          totalElements: displayData.totalElements,
          totalPages: displayData.totalPages,
        }}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onEdit={row => {
          reset(row);
          setSelectedRow(row);
          setFormMode('edit');
        }}
        showActions
      />
    </section>
  );
}
